import { useEffect, useState } from "react";
import PageShell from "../components/website-Generator/PageShell";
import {
    renderDevPreview,
    generatePublicPreviewQa,
    getPublicPreviewQa,
    getPublicPreviewQaDocument,
    getPublicPreviewQaAsset,
    getPublicPreviewQaDiagnostics,
    waitForPublicPreviewQa,
    getProjectCrmRenderQa,
    getDevDiagnosticProjects,
    generateV2PreviewQa,
    getV2PreviewQa,
    getV2PreviewQaDocument,
    getV2PreviewQaAsset,
    compareV2PreviewQa,
    claimV2PreviewQa,
    generateV2VisualPreview
} from "../services/devRenderService";

import {
    normalizeGeneratedPages,
    runGenerationQa
} from "../utils/qaPlaygroundUtils";

export const V2_SMOKE_TEST_PAYLOADS = {
    fashion: {
        businessName: "Urban Threads Store",
        industry: "Ecommerce",
        subIndustry: "Clothing Store",
        description: "Fashion store for hoodies, jeans, t-shirts, kurtis, western wear, dresses and seasonal outfits.",
        themeKey: "premium-dark",
        desiredLayoutStyle: "commerce",
        visualTone: "premium",
        preferredSectionDensity: "rich",
        brandStyle: "Modern",
        tone: "Bold",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    },

    jewellery: {
        businessName: "Kundan Palace",
        industry: "Ecommerce",
        subIndustry: "",
        description: "Jewellery shop selling gold rings, diamond necklaces, bridal jewellery, earrings, bangles and festive collections.",
        themeKey: "premium-dark",
        desiredLayoutStyle: "commerce",
        visualTone: "premium",
        preferredSectionDensity: "rich",
        brandStyle: "Luxury",
        tone: "Elegant",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    },

    kidsStore: {
        businessName: "Tiny Steps Store",
        industry: "Ecommerce",
        subIndustry: "",
        description: "Kids store for baby clothes, toys, school bags, newborn gift sets, girls dresses, boys outfits and playful accessories.",
        themeKey: "clean-light",
        desiredLayoutStyle: "commerce",
        visualTone: "friendly",
        preferredSectionDensity: "rich",
        brandStyle: "Playful",
        tone: "Friendly",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    },

    cosmetics: {
        businessName: "Glow Box Beauty",
        industry: "Ecommerce",
        subIndustry: "",
        description: "Cosmetics store selling skincare, makeup kits, lipsticks, foundations, beauty tools, serums and premium self-care products.",
        themeKey: "premium-dark",
        desiredLayoutStyle: "commerce",
        visualTone: "premium",
        preferredSectionDensity: "rich",
        brandStyle: "Modern Beauty",
        tone: "Elegant",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    },

    electronics: {
        businessName: "Tech Hub Store",
        industry: "Ecommerce",
        subIndustry: "",
        description: "Electronics shop selling mobile phones, laptops, headphones, chargers, smart watches, bluetooth speakers and useful gadgets.",
        themeKey: "clean-light",
        desiredLayoutStyle: "commerce",
        visualTone: "modern",
        preferredSectionDensity: "rich",
        brandStyle: "Tech",
        tone: "Professional",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    },

    accessories: {
        businessName: "Style Addons",
        industry: "Ecommerce",
        subIndustry: "",
        description: "Accessories store for handbags, belts, sunglasses, watches, wallets, fashion jewellery and everyday styling add-ons.",
        themeKey: "premium-dark",
        desiredLayoutStyle: "commerce",
        visualTone: "premium",
        preferredSectionDensity: "rich",
        brandStyle: "Stylish",
        tone: "Modern",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    },

    giftStore: {
        businessName: "GiftNest",
        industry: "Ecommerce",
        subIndustry: "",
        description: "Gift store for birthday gifts, anniversary hampers, personalized mugs, greeting cards, festive boxes and corporate gifts.",
        themeKey: "clean-light",
        desiredLayoutStyle: "commerce",
        visualTone: "warm",
        preferredSectionDensity: "rich",
        brandStyle: "Warm",
        tone: "Friendly",
        goalOfWebsite: "Drive Sales",
        callToActionType: "Shop Now"
    }
};

/* keep your existing samples object here */
const samples = {
    academy: {
        label: "Academy",
        payload: {
            businessName: "Shriansh Football Academy",
            industry: "Education",
            subIndustry: "Sports Academy",
            themeKey: "premium-dark",
            desiredLayoutStyle: "premium",
            visualTone: "modern",
            preferredSectionDensity: "balanced",
            brandStyle: "Premium",
            toneOfVoice: "Professional",
            primaryGoal: "Generate Leads",
            primaryCallToAction: "Book a Trial",
            debugVariants: {
                "academy-home-hero": 2,
                "academy-home-programs": 2,
                "academy-home-categories": 2,
                "academy-home-coaches": 2,
                "academy-home-process": 2,
                "academy-home-cta": 2
            },
            data: {
                brand: {
                    name: "Shriansh Football Academy",
                    tagline: "Train smarter. Play stronger."
                },
                meta: {
                    themeKey: "premium-dark"
                },
                hero: {
                    eyebrow: "Elite Football Training",
                    title: "Professional Football Coaching for Young Players",
                    subtitle: "Structured programs, expert coaches, and performance-focused training for every age group.",
                    primaryCta: "Book a Trial",
                    secondaryCta: "View Programs",
                    trustLine: "Trusted by 500+ students and parents"
                },
                services: [
                    {
                        title: "Beginner Football Program",
                        description: "Foundation-level coaching for young players starting their football journey."
                    },
                    {
                        title: "Advanced Match Training",
                        description: "Tactical, fitness, and skill-based training for competitive players."
                    },
                    {
                        title: "Weekend Coaching Camp",
                        description: "Flexible weekend sessions for school students and working parents."
                    }
                ],
                features: [
                    {
                        title: "Certified Coaches",
                        description: "Learn from experienced football trainers."
                    },
                    {
                        title: "Age-wise Batches",
                        description: "Programs designed for each age and skill level."
                    },
                    {
                        title: "Performance Tracking",
                        description: "Regular feedback and progress reports."
                    }
                ],
                cta: {
                    title: "Ready to start training?",
                    subtitle: "Book your first trial session today.",
                    buttonText: "Book Trial"
                },
                ShowcaseItems: [
                    {
                        Title: "Foundation Football Program",
                        Subtitle: "Age 6–10",
                        Description: "Core football skills, movement, passing, balance, and confidence building.",
                        Badge: "Beginner",
                        Meta: ["6–10 years", "Mon, Wed, Fri", "Main Turf Ground"]
                    },
                    {
                        Title: "Advanced Match Training",
                        Subtitle: "Age 11–16",
                        Description: "Tactical match preparation, stamina, position training, and game intelligence.",
                        Badge: "Advanced",
                        Meta: ["11–16 years", "Tue, Thu, Sat", "Performance Ground"]
                    }
                ],
                DetailDensity: {
                    Academy: {
                        Programs: [
                            {
                                Title: "Foundation Football Program",
                                Subtitle: "Beginner training",
                                Description: "Core football skills, movement, passing, balance, and confidence building.",
                                AgeGroup: "6–10 years",
                                BatchType: "Beginner",
                                Schedule: "Mon, Wed, Fri",
                                Venue: "Main Turf Ground",
                                CtaText: "Book Trial",
                                Tags: ["Ball control", "Fitness", "Team play"]
                            },
                            {
                                Title: "Advanced Match Training",
                                Subtitle: "Competitive batch",
                                Description: "Tactical match preparation, stamina, position training, and game intelligence.",
                                AgeGroup: "11–16 years",
                                BatchType: "Advanced",
                                Schedule: "Tue, Thu, Sat",
                                Venue: "Performance Ground",
                                CtaText: "Book Trial",
                                Tags: ["Tactics", "Match play", "Performance"]
                            }
                        ],
                        Coaches: [],
                        Facilities: []
                    }
                }
            }
        }
    },

    realEstate: {
        label: "Real Estate",
        payload: {
            businessName: "Skyline Realty",
            industry: "Real Estate",
            subIndustry: "Property Agency",
            themeKey: "corporate-clean",
            desiredLayoutStyle: "realestate-company",
            templatePackKey: "realestate-company",
            verticalKey: "realestate-company",
            primaryEntityType: "properties",
            contentStyleHint: "realestate-company",
            visualTone: "premium",
            preferredSectionDensity: "rich",
            brandStyle: "Corporate",
            toneOfVoice: "Professional",
            primaryGoal: "Generate Leads",
            primaryCallToAction: "Explore Properties",
            debugVariants: {
                "realestate-company-home-hero": 2,
                "realestate-company-home-market": 2,
                "realestate-company-home-listings": 2,
                "realestate-company-home-advisory": 2,
                "realestate-company-home-services": 2,
                "realestate-company-home-cta": 2
            },
            data: {
                brand: {
                    name: "Skyline Realty",
                    tagline: "Find your next perfect property."
                },
                meta: {
                    themeKey: "corporate-clean"
                },
                hero: {
                    eyebrow: "Premium Property Advisory",
                    title: "Find Verified Homes, Offices, and Investment Properties",
                    subtitle: "Discover curated listings with expert local guidance, transparent pricing, and fast site visits.",
                    primaryCta: "Explore Properties",
                    secondaryCta: "Book Consultation",
                    trustLine: "500+ verified listings across prime locations"
                },
                services: [
                    {
                        title: "Residential Properties",
                        description: "Apartments, villas, and premium homes matched to your lifestyle."
                    },
                    {
                        title: "Commercial Spaces",
                        description: "Offices, retail spaces, and investment-ready commercial assets."
                    },
                    {
                        title: "Property Consultation",
                        description: "Location, pricing, legal, and investment guidance from experts."
                    }
                ],
                features: [
                    {
                        title: "Verified Listings",
                        description: "Every property is checked before recommendation."
                    },
                    {
                        title: "Local Market Experts",
                        description: "Guidance based on city-level pricing and demand trends."
                    },
                    {
                        title: "Fast Site Visits",
                        description: "Schedule property visits quickly with our advisory team."
                    }
                ],
                cta: {
                    title: "Looking for the right property?",
                    subtitle: "Talk to our property advisor today.",
                    buttonText: "Book Consultation"
                },
                "ShowcaseItems": [
                    {
                        "Title": "3BHK Premium Apartment",
                        "Subtitle": "Satellite",
                        "Description": "Spacious apartment with modern amenities.",
                        "Meta": ["₹85L", "3 BHK", "1200 sqft"],
                        "Badge": "Verified"
                    },
                    {
                        "Title": "Luxury Villa",
                        "Subtitle": "Bopal",
                        "Description": "Independent villa with garden.",
                        "Meta": ["₹1.8Cr", "4 BHK", "3200 sqft"],
                        "Badge": "Premium"
                    }
                ]
            }
        }
    },

    commerce: {
        label: "Commerce",
        payload: {
            businessName: "Urban Threads Store",
            industry: "Ecommerce",
            subIndustry: "Clothing Store",
            themeKey: "premium-dark",
            desiredLayoutStyle: "commerce",
            visualTone: "premium",
            preferredSectionDensity: "rich",
            brandStyle: "Modern",
            toneOfVoice: "Bold",
            primaryGoal: "Drive Sales",
            primaryCallToAction: "Shop Now",

            debugVariants: {
                "commerce-home-hero": 2,
                "commerce-home-products": 2,
                "commerce-home-categorystrip": 2,
                "commerce-home-collections": 2,
                "commerce-home-truststrip": 2,
                "commerce-home-cta": 2
            },

            data: {
                brand: {
                    name: "Urban Threads",
                    tagline: "Style that speaks for you."
                },

                meta: {
                    themeKey: "premium-dark"
                },

                hero: {
                    eyebrow: "New Collection",
                    title: "Premium Streetwear for Everyday Style",
                    subtitle: "Discover curated outfits, limited drops, and modern fashion essentials.",
                    primaryCta: "Shop Now",
                    secondaryCta: "Explore Products",
                    trustLine: "Trusted by 10,000+ customers"
                },

                ShowcaseItems: [
                    {
                        Title: "Classic Black Hoodie",
                        Subtitle: "Winter Collection",
                        Description: "Premium cotton hoodie designed for comfort and street style.",
                        Meta: ["₹1,999", "Hoodie", "In Stock"]
                    },
                    {
                        Title: "Slim Fit Denim Jeans",
                        Subtitle: "Denim Series",
                        Description: "Stretchable slim-fit jeans with modern finish.",
                        Meta: ["₹2,499", "Jeans", "Limited Stock"]
                    },
                    {
                        Title: "Oversized Graphic T-Shirt",
                        Subtitle: "Summer Drop",
                        Description: "Relaxed fit t-shirt with bold graphic prints.",
                        Meta: ["₹1,199", "T-Shirt", "In Stock"]
                    }
                ],

                DetailDensity: {
                    Commerce: {
                        Products: [
                            {
                                Title: "Classic Black Hoodie",
                                Description: "Premium cotton hoodie designed for comfort and street style.",
                                PriceLabel: "₹1,999",
                                Category: "Hoodie",
                                StockLabel: "In Stock",
                                DiscountLabel: "20% OFF",
                                Tags: ["Cotton", "Winter", "Streetwear"]
                            },
                            {
                                Title: "Slim Fit Denim Jeans",
                                Description: "Stretchable slim-fit jeans with modern finish.",
                                PriceLabel: "₹2,499",
                                Category: "Jeans",
                                StockLabel: "Limited Stock",
                                DiscountLabel: "",
                                Tags: ["Denim", "Casual", "Stretch"]
                            }
                        ]
                    }
                }
            }
        }
    },

    legal: {
        label: "Legal",
        payload: {
            businessName: "Veritas Legal Partners",
            industry: "Legal",
            subIndustry: "Corporate Law Firm",
            themeKey: "luxury-light",
            desiredLayoutStyle: "trust-first",
            visualTone: "authoritative",
            preferredSectionDensity: "balanced",
            brandStyle: "Premium",
            toneOfVoice: "Professional",
            primaryGoal: "Book Appointments",
            primaryCallToAction: "Book Legal Consultation",
            data: {
                brand: {
                    name: "Veritas Legal Partners",
                    tagline: "Clear legal advice for complex decisions."
                },
                meta: {
                    themeKey: "luxury-light"
                },
                hero: {
                    eyebrow: "Corporate & Business Law",
                    title: "Trusted Legal Support for Companies, Founders, and Families",
                    subtitle: "Practical legal guidance across contracts, compliance, disputes, and advisory matters.",
                    primaryCta: "Book Legal Consultation",
                    secondaryCta: "View Practice Areas",
                    trustLine: "Trusted by startups, SMEs, and business owners"
                },
                services: [
                    {
                        title: "Corporate Advisory",
                        description: "Legal guidance for business formation, agreements, compliance, and governance."
                    },
                    {
                        title: "Contract Drafting",
                        description: "Clear, enforceable contracts for vendors, employees, partners, and clients."
                    },
                    {
                        title: "Dispute Resolution",
                        description: "Strategic support for commercial, civil, and business disputes."
                    }
                ],
                features: [
                    {
                        title: "Experienced Counsel",
                        description: "Work with lawyers who understand practical business risk."
                    },
                    {
                        title: "Confidential Process",
                        description: "Your legal matters are handled with privacy and care."
                    },
                    {
                        title: "Clear Next Steps",
                        description: "No confusing language. Just practical guidance."
                    }
                ],
                cta: {
                    title: "Need legal clarity?",
                    subtitle: "Schedule a confidential consultation.",
                    buttonText: "Book Consultation"
                },
                DetailDensity: {
                    Legal: {
                        PracticeAreas: [
                            {
                                Title: "Corporate Advisory",
                                Description: "Business formation, compliance, governance, and company documentation."
                            },
                            {
                                Title: "Contract Drafting",
                                Description: "Vendor, employee, partner, and client agreements drafted with clarity."
                            },
                            {
                                Title: "Dispute Resolution",
                                Description: "Strategic support for commercial, civil, and business disputes."
                            }
                        ],
                        Lawyers: [
                            {
                                Name: "Adv. Riya Mehta",
                                Role: "Corporate Lawyer",
                                Bio: "Advises startups and SMEs on contracts, governance, and compliance.",
                                Highlights: ["Corporate law", "Contracts", "Compliance"]
                            },
                            {
                                Name: "Adv. Arjun Shah",
                                Role: "Dispute Resolution Counsel",
                                Bio: "Supports businesses and families with civil and commercial disputes.",
                                Highlights: ["Civil disputes", "Commercial matters", "Negotiation"]
                            }
                        ]
                    }
                }
            }
        }
    },

    saas: {
        label: "SaaS",
        payload: {
            businessName: "FlowDesk AI",
            industry: "SaaS",
            subIndustry: "AI Productivity Platform",
            themeKey: "neo-gradient",
            desiredLayoutStyle: "saas",
            visualTone: "modern",
            preferredSectionDensity: "balanced",
            brandStyle: "Modern",
            toneOfVoice: "Bold",
            primaryGoal: "Book Demo",
            primaryCallToAction: "Book a Demo",
            data: {
                brand: {
                    name: "FlowDesk AI",
                    tagline: "Automate your team workflows."
                },
                meta: {
                    themeKey: "neo-gradient"
                },
                hero: {
                    eyebrow: "AI Workflow Automation",
                    title: "Automate Repetitive Work and Move Faster With AI",
                    subtitle: "FlowDesk AI helps teams manage tasks, approvals, documents, and customer workflows from one intelligent workspace.",
                    primaryCta: "Book a Demo",
                    secondaryCta: "See Features",
                    trustLine: "Built for growing teams and modern businesses"
                },
                services: [
                    {
                        title: "Workflow Automation",
                        description: "Automate approval flows, task routing, and internal operations."
                    },
                    {
                        title: "AI Document Assistant",
                        description: "Generate, summarize, and manage business documents faster."
                    },
                    {
                        title: "Team Dashboard",
                        description: "Track tasks, bottlenecks, and performance from one place."
                    }
                ],
                features: [
                    {
                        title: "AI-Powered Actions",
                        description: "Let AI suggest next steps and automate repetitive work."
                    },
                    {
                        title: "Role-Based Access",
                        description: "Control what each team member can view and manage."
                    },
                    {
                        title: "Real-Time Insights",
                        description: "Understand workflow performance instantly."
                    }
                ],
                cta: {
                    title: "Ready to automate your workflow?",
                    subtitle: "Book a demo and see FlowDesk AI in action.",
                    buttonText: "Book Demo"
                }
            }
        }
    },

    healthcare: {
        label: "Healthcare",
        payload: {
            businessName: "CarePlus Clinic",
            industry: "Healthcare",
            subIndustry: "Multi-Specialty Clinic",
            themeKey: "corporate-clean",
            desiredLayoutStyle: "healthcare",
            visualTone: "trustworthy",
            preferredSectionDensity: "balanced",
            brandStyle: "Professional",
            toneOfVoice: "Caring",
            primaryGoal: "Book Appointments",
            primaryCallToAction: "Book Appointment",
            debugVariants: {
                "healthcare-home-treatments": 2,
                "healthcare-home-doctors": 2,
                "healthcare-home-facilities": 2
            },
            data: {
                brand: { name: "CarePlus Clinic", tagline: "Patient-first care for every family." },
                meta: { themeKey: "corporate-clean" },
                hero: {
                    eyebrow: "Trusted Healthcare",
                    title: "Personalized Medical Care for Families and Professionals",
                    subtitle: "Consult experienced doctors, access essential treatments, and book appointments with ease.",
                    primaryCta: "Book Appointment",
                    secondaryCta: "View Doctors",
                    trustLine: "Trusted by 20,000+ patients"
                },
                features: [
                    { title: "Experienced Doctors", description: "Consult specialists across common care areas." },
                    { title: "Easy Appointments", description: "Book consultations quickly with minimal waiting." },
                    { title: "Modern Facilities", description: "Comfortable clinic environment and patient-first support." }
                ],
                services: [
                    { title: "General Consultation", description: "Diagnosis and guidance for common health concerns." },
                    { title: "Preventive Health Checkups", description: "Routine screening and health monitoring." },
                    { title: "Specialist Consultation", description: "Care from experienced specialists by appointment." }
                ],
                cta: {
                    title: "Need medical guidance?",
                    subtitle: "Book an appointment with our care team.",
                    buttonText: "Book Appointment"
                },
                DetailDensity: {
                    Healthcare: {
                        Treatments: [
                            {
                                Title: "General Consultation",
                                Subtitle: "Primary Care",
                                Description: "Routine diagnosis, prescriptions, and care guidance for common health issues.",
                                BestFor: "Families",
                                CtaText: "Book Appointment"
                            },
                            {
                                Title: "Preventive Health Checkup",
                                Subtitle: "Screening",
                                Description: "Basic screening packages to track health indicators and reduce future risk.",
                                BestFor: "Adults",
                                CtaText: "Book Checkup"
                            }
                        ],
                        Doctors: [
                            {
                                Name: "Dr. Aditi Sharma",
                                Specialty: "General Physician",
                                Bio: "Experienced physician focused on family care and preventive health.",
                                Availability: "Mon-Sat",
                                Highlights: ["10+ years experience", "Family medicine", "Preventive care"]
                            },
                            {
                                Name: "Dr. Rohan Mehta",
                                Specialty: "Internal Medicine",
                                Bio: "Specialist in lifestyle disorders, chronic care, and health management.",
                                Availability: "Tue-Sun",
                                Highlights: ["Chronic care", "Lifestyle health", "Patient-first approach"]
                            }
                        ],
                        Facilities: ["Consultation Rooms", "Diagnostics Support", "Pharmacy Assistance"]
                    }
                }
            }
        }
    },

    restaurant: {
        label: "Restaurant / Cafe",
        payload: {
            businessName: "Saffron Table Cafe",
            industry: "Restaurant",
            subIndustry: "Cafe / Casual Dining",
            themeKey: "warm-premium",
            desiredLayoutStyle: "restaurant",
            visualTone: "warm",
            preferredSectionDensity: "balanced",
            brandStyle: "Premium",
            toneOfVoice: "Friendly",
            primaryGoal: "Get Reservations",
            primaryCallToAction: "Reserve a Table",
            debugVariants: {
                "restaurant-home-hero": 2,
                "restaurant-home-menu": 2,
                "restaurant-home-specials": 2,
                "restaurant-home-ambience": 2,
                "restaurant-home-cta": 2
            },
            data: {
                brand: {
                    name: "Saffron Table Cafe",
                    tagline: "Fresh food, warm moments."
                },
                meta: {
                    themeKey: "warm-premium"
                },
                hero: {
                    eyebrow: "Fresh • Local • Handcrafted",
                    title: "A Cozy Cafe for Fresh Meals, Coffee, and Good Conversations",
                    subtitle: "Enjoy chef-crafted dishes, signature beverages, and a relaxing dining experience for families, friends, and professionals.",
                    primaryCta: "Reserve a Table",
                    secondaryCta: "View Menu",
                    trustLine: "Loved by 5,000+ local guests"
                },
                services: [
                    {
                        title: "All-Day Dining",
                        description: "Fresh breakfast, lunch, dinner, and quick bites served daily."
                    },
                    {
                        title: "Signature Coffee",
                        description: "Handcrafted hot and cold beverages made with premium beans."
                    },
                    {
                        title: "Private Events",
                        description: "Host birthdays, small gatherings, and celebrations in a cozy setting."
                    }
                ],
                features: [
                    {
                        title: "Fresh Ingredients",
                        description: "Seasonal ingredients sourced for taste and quality."
                    },
                    {
                        title: "Cozy Ambience",
                        description: "Comfortable seating, warm interiors, and a relaxed atmosphere."
                    },
                    {
                        title: "Quick Reservations",
                        description: "Reserve tables easily for lunch, dinner, or special occasions."
                    }
                ],
                cta: {
                    title: "Planning your next meal out?",
                    subtitle: "Reserve your table and enjoy a fresh dining experience.",
                    buttonText: "Reserve Table"
                },
                ShowcaseItems: [
                    {
                        Title: "Paneer Tikka Platter",
                        Subtitle: "Chef Special",
                        Description: "Smoky grilled paneer served with mint chutney and fresh salad.",
                        Badge: "Popular",
                        Meta: ["₹349", "Veg", "Chef Special"]
                    },
                    {
                        Title: "Classic Cold Coffee",
                        Subtitle: "Signature Drink",
                        Description: "Creamy chilled coffee topped with chocolate and whipped cream.",
                        Badge: "Bestseller",
                        Meta: ["₹179", "Cafe Drink", "Cold"]
                    },
                    {
                        Title: "Wood-Fired Farmhouse Pizza",
                        Subtitle: "Fresh Oven",
                        Description: "Loaded with fresh vegetables, cheese, herbs, and crispy crust.",
                        Badge: "New",
                        Meta: ["₹499", "Pizza", "Serves 2"]
                    }
                ],
                DetailDensity: {
                    Restaurant: {
                        MenuItems: [
                            {
                                Title: "Paneer Tikka Platter",
                                Subtitle: "Chef Special",
                                Description: "Smoky grilled paneer served with mint chutney and fresh salad.",
                                PriceLabel: "₹349",
                                Category: "Starters",
                                Badge: "Popular",
                                Tags: ["Veg", "Grilled", "Spicy"]
                            },
                            {
                                Title: "Wood-Fired Farmhouse Pizza",
                                Subtitle: "Fresh Oven",
                                Description: "Loaded with vegetables, cheese, herbs, and crispy crust.",
                                PriceLabel: "₹499",
                                Category: "Pizza",
                                Badge: "New",
                                Tags: ["Veg", "Cheesy", "Fresh"]
                            }
                        ],
                        Specials: [
                            {
                                Title: "Weekend Brunch Combo",
                                Description: "A complete brunch plate with beverage and dessert.",
                                PriceLabel: "₹599",
                                Badge: "Weekend"
                            }
                        ]
                    }
                }
            }
        }
    },

    fitness: {
        label: "Fitness / Gym",
        payload: {
            businessName: "IronCore Fitness Studio",
            verticalKey: "fitness",
            templatePackKey: "fitness",
            primaryEntityType: "fitness-programs",
            contentStyleHint: "fitness",
            industry: "Fitness",
            subIndustry: "Gym / Personal Training",
            themeKey: "premium-dark",
            desiredLayoutStyle: "fitness",
            visualTone: "bold",
            preferredSectionDensity: "balanced",
            brandStyle: "Modern",
            toneOfVoice: "Motivational",
            primaryGoal: "Generate Leads",
            primaryCallToAction: "Book Free Trial",
            debugVariants: {
                "fitness-home-hero": 2,
                "fitness-home-programs": 2,
                "fitness-home-trainers": 2,
                "fitness-home-transformations": 2,
                "fitness-home-cta": 2
            },
            data: {
                brand: {
                    name: "IronCore Fitness Studio",
                    tagline: "Train hard. Move better. Feel stronger."
                },
                meta: {
                    themeKey: "premium-dark"
                },
                hero: {
                    eyebrow: "Strength • Conditioning • Transformation",
                    title: "Personalized Fitness Training for Real Results",
                    subtitle: "Join expert-led workouts, structured fitness programs, and goal-based coaching for strength, stamina, and body transformation.",
                    primaryCta: "Book Free Trial",
                    secondaryCta: "View Programs",
                    trustLine: "1,000+ transformations completed"
                },
                services: [
                    {
                        title: "Strength Training",
                        description: "Build strength with structured progressive workout plans."
                    },
                    {
                        title: "Weight Loss Program",
                        description: "Goal-based fat-loss plans with workouts and nutrition guidance."
                    },
                    {
                        title: "Personal Training",
                        description: "One-on-one coaching for faster, safer, and focused progress."
                    }
                ],
                features: [
                    {
                        title: "Certified Trainers",
                        description: "Train with experienced coaches who guide every session."
                    },
                    {
                        title: "Modern Equipment",
                        description: "Access premium machines, free weights, and functional training zones."
                    },
                    {
                        title: "Progress Tracking",
                        description: "Track measurements, strength gains, and fitness milestones."
                    }
                ],
                cta: {
                    title: "Ready to start your fitness journey?",
                    subtitle: "Book a free trial session today.",
                    buttonText: "Book Free Trial"
                },
                ShowcaseItems: [
                    {
                        Title: "Strength Builder Program",
                        Subtitle: "Muscle & Power",
                        Description: "Progressive strength training for beginners and intermediate members.",
                        Badge: "Popular",
                        Meta: ["8 weeks", "Strength", "Trainer-led"]
                    },
                    {
                        Title: "Fat Loss Transformation",
                        Subtitle: "Weight Loss",
                        Description: "Structured workouts, cardio plans, and nutrition support.",
                        Badge: "Transformation",
                        Meta: ["12 weeks", "Fat Loss", "Tracking"]
                    }
                ],
                DetailDensity: {
                    Fitness: {
                        Programs: [
                            {
                                Title: "Strength Builder Program",
                                Subtitle: "Muscle & Power",
                                Description: "Progressive strength training for beginners and intermediate members.",
                                Duration: "8 weeks",
                                Level: "Beginner to Intermediate",
                                CtaText: "Book Free Trial",
                                Tags: ["Strength", "Weights", "Coaching"]
                            },
                            {
                                Title: "Fat Loss Transformation",
                                Subtitle: "Weight Loss",
                                Description: "Structured workouts, cardio plans, and nutrition support.",
                                Duration: "12 weeks",
                                Level: "All levels",
                                CtaText: "Start Transformation",
                                Tags: ["Fat Loss", "Cardio", "Nutrition"]
                            }
                        ],
                        Trainers: [
                            {
                                Name: "Rahul Mehta",
                                Role: "Strength Coach",
                                Bio: "Specialist in strength training, mobility, and beginner transformations.",
                                Highlights: ["Strength", "Mobility", "Transformation"]
                            },
                            {
                                Name: "Neha Shah",
                                Role: "Fat Loss Coach",
                                Bio: "Helps members build consistency with workouts and nutrition habits.",
                                Highlights: ["Fat Loss", "HIIT", "Nutrition"]
                            }
                        ]
                    }
                }
            }
        }
    },

    beauty: {
        label: "Beauty / Salon",
        payload: {
            businessName: "GlowNest Beauty Lounge",
            industry: "Beauty",
            subIndustry: "Salon / Spa",
            themeKey: "luxury-light",
            desiredLayoutStyle: "beauty",
            visualTone: "elegant",
            preferredSectionDensity: "balanced",
            brandStyle: "Luxury",
            toneOfVoice: "Warm",
            primaryGoal: "Book Appointments",
            primaryCallToAction: "Book Appointment",
            debugVariants: {
                "beauty-home-hero": 2,
                "beauty-home-services": 2,
                "beauty-home-specialists": 2,
                "beauty-home-gallery": 2,
                "beauty-home-cta": 2
            },
            data: {
                brand: {
                    name: "GlowNest Beauty Lounge",
                    tagline: "Look fresh. Feel confident."
                },
                meta: {
                    themeKey: "luxury-light"
                },
                hero: {
                    eyebrow: "Salon • Spa • Beauty Care",
                    title: "Premium Beauty Services for Hair, Skin, and Bridal Looks",
                    subtitle: "Experience expert styling, skin care, makeup, and spa services in a calm, premium salon environment.",
                    primaryCta: "Book Appointment",
                    secondaryCta: "View Services",
                    trustLine: "Trusted by 3,000+ happy clients"
                },
                services: [
                    {
                        title: "Hair Styling & Treatment",
                        description: "Haircuts, styling, coloring, spa, and repair treatments."
                    },
                    {
                        title: "Skin & Facial Care",
                        description: "Glow facials, cleanup, skin care, and relaxation treatments."
                    },
                    {
                        title: "Bridal Makeup",
                        description: "Elegant bridal looks, party makeup, and event styling."
                    }
                ],
                features: [
                    {
                        title: "Expert Stylists",
                        description: "Experienced beauty professionals for every service."
                    },
                    {
                        title: "Premium Products",
                        description: "Quality products used for safer, lasting results."
                    },
                    {
                        title: "Relaxing Experience",
                        description: "Clean, calm, and comfortable salon environment."
                    }
                ],
                cta: {
                    title: "Ready for your glow-up?",
                    subtitle: "Book your beauty appointment today.",
                    buttonText: "Book Appointment"
                },
                ShowcaseItems: [
                    {
                        Title: "Luxury Hair Spa",
                        Subtitle: "Hair Care",
                        Description: "Deep conditioning treatment for smoother, shinier, healthier hair.",
                        Badge: "Popular",
                        Meta: ["₹1,499", "60 min", "Hair"]
                    },
                    {
                        Title: "Glow Facial",
                        Subtitle: "Skin Care",
                        Description: "Refreshing facial designed for clean, bright, and hydrated skin.",
                        Badge: "Bestseller",
                        Meta: ["₹1,999", "75 min", "Skin"]
                    }
                ],
                DetailDensity: {
                    Beauty: {
                        Services: [
                            {
                                Title: "Luxury Hair Spa",
                                Subtitle: "Hair Care",
                                Description: "Deep conditioning treatment for smoother and healthier hair.",
                                PriceLabel: "₹1,499",
                                Duration: "60 min",
                                CtaText: "Book Now",
                                Tags: ["Hair", "Spa", "Repair"]
                            },
                            {
                                Title: "Glow Facial",
                                Subtitle: "Skin Care",
                                Description: "Refreshing facial designed for clean, bright, and hydrated skin.",
                                PriceLabel: "₹1,999",
                                Duration: "75 min",
                                CtaText: "Book Now",
                                Tags: ["Skin", "Glow", "Hydration"]
                            }
                        ],
                        Specialists: [
                            {
                                Name: "Priya Sharma",
                                Role: "Senior Stylist",
                                Bio: "Specialist in hair styling, coloring, and premium treatments.",
                                Highlights: ["Hair styling", "Coloring", "Hair spa"]
                            },
                            {
                                Name: "Kavya Patel",
                                Role: "Skin & Makeup Artist",
                                Bio: "Experienced in facials, bridal makeup, and event styling.",
                                Highlights: ["Facials", "Bridal", "Makeup"]
                            }
                        ]
                    }
                }
            }
        }
    },

    realEstatePortal: {
        label: "Real Estate Portal",
        payload: {
            businessName: "PropHub Listings",
            industry: "Real Estate",
            subIndustry: "Property Portal",
            themeKey: "corporate-clean",
            desiredLayoutStyle: "marketplace",
            visualTone: "premium",
            preferredSectionDensity: "rich",
            brandStyle: "Corporate",
            toneOfVoice: "Professional",
            primaryGoal: "Generate Leads",
            primaryCallToAction: "Search Properties",
            debugVariants: {
                "realestate-portal-home-hero": 2,
                "realestate-portal-home-search": 2,
                "realestate-portal-home-listings": 2,
                "realestate-portal-home-cities": 2,
                "realestate-portal-home-cta": 2
            },
            data: {
                brand: {
                    name: "PropHub Listings",
                    tagline: "Search verified properties faster."
                },
                meta: {
                    themeKey: "corporate-clean"
                },
                hero: {
                    eyebrow: "Verified Property Marketplace",
                    title: "Search Homes, Offices, and Investment Properties in One Place",
                    subtitle: "Browse verified listings, compare locations, and connect with property advisors for fast site visits.",
                    primaryCta: "Search Properties",
                    secondaryCta: "List Your Property",
                    trustLine: "1,200+ verified properties listed"
                },
                services: [
                    {
                        title: "Buy Properties",
                        description: "Explore verified residential and commercial listings."
                    },
                    {
                        title: "Rent Properties",
                        description: "Find ready-to-move homes, offices, and retail spaces."
                    },
                    {
                        title: "List Property",
                        description: "Let owners and agents publish verified property listings."
                    }
                ],
                features: [
                    {
                        title: "Smart Search",
                        description: "Filter by budget, location, size, and property type."
                    },
                    {
                        title: "Verified Listings",
                        description: "Reduce fake leads with quality-checked property information."
                    },
                    {
                        title: "Advisor Connect",
                        description: "Get quick help for site visits and property shortlisting."
                    }
                ],
                cta: {
                    title: "Want to find the right property faster?",
                    subtitle: "Start searching verified listings today.",
                    buttonText: "Search Properties"
                },
                ShowcaseItems: [
                    {
                        Title: "2BHK Apartment near SG Highway",
                        Subtitle: "Ahmedabad",
                        Description: "Ready-to-move apartment with parking and modern amenities.",
                        Badge: "Verified",
                        Meta: ["₹58L", "2 BHK", "950 sqft"]
                    },
                    {
                        Title: "Commercial Office Space",
                        Subtitle: "Prahlad Nagar",
                        Description: "Premium office space suitable for startups and growing teams.",
                        Badge: "Commercial",
                        Meta: ["₹75,000/mo", "Office", "1400 sqft"]
                    },
                    {
                        Title: "Premium Villa Plot",
                        Subtitle: "Bopal",
                        Description: "Gated community villa plot in a fast-growing residential location.",
                        Badge: "Investment",
                        Meta: ["₹1.2Cr", "Plot", "240 sq yd"]
                    }
                ],
                DetailDensity: {
                    RealEstate: {
                        Listings: [
                            {
                                Title: "2BHK Apartment near SG Highway",
                                Subtitle: "Ahmedabad",
                                Description: "Ready-to-move apartment with parking and modern amenities.",
                                PriceLabel: "₹58L",
                                PropertyType: "Apartment",
                                Location: "SG Highway",
                                AreaLabel: "950 sqft",
                                Badge: "Verified",
                                Tags: ["2 BHK", "Ready to move", "Parking"]
                            },
                            {
                                Title: "Commercial Office Space",
                                Subtitle: "Prahlad Nagar",
                                Description: "Premium office space suitable for startups and growing teams.",
                                PriceLabel: "₹75,000/mo",
                                PropertyType: "Office",
                                Location: "Prahlad Nagar",
                                AreaLabel: "1400 sqft",
                                Badge: "Commercial",
                                Tags: ["Office", "Furnished", "Prime location"]
                            }
                        ],
                        Cities: ["Ahmedabad", "Gandhinagar", "Vadodara", "Surat"]
                    }
                }
            }
        }
    },
};

const variantConfigs = {
    realEstate: [
        "realestate-company-home-hero",
        "realestate-company-home-market",
        "realestate-company-home-listings",
        "realestate-company-home-advisory",
        "realestate-company-home-services",
        "realestate-company-home-cta"
    ],

    realEstatePortal: [
        "realestate-portal-home-hero",
        "realestate-portal-home-search",
        "realestate-portal-home-listings",
        "realestate-portal-home-cities",
        "realestate-portal-home-cta"
    ],

    academy: [
        "academy-home-hero",
        "academy-home-programs",
        "academy-home-categories",
        "academy-home-coaches",
        "academy-home-process",
        "academy-home-cta"
    ],

    commerce: [
        "commerce-home-hero",
        "commerce-home-products",
        "commerce-home-categorystrip",
        "commerce-home-collections",
        "commerce-home-truststrip",
        "commerce-home-cta"
    ],

    saas: [
        "saas-home-features",
        "saas-home-integrations",
        "saas-home-pricing"
    ],

    legal: [
        "legal-home-hero",
        "legal-home-practiceareas",
        "legal-home-lawyers",
        "legal-home-cta"
    ],

    healthcare: [
        "healthcare-home-treatments",
        "healthcare-home-doctors",
        "healthcare-home-facilities"
    ],

    restaurant: [
        "restaurant-home-hero",
        "restaurant-home-menu",
        "restaurant-home-specials",
        "restaurant-home-ambience",
        "restaurant-home-cta"
    ],

    fitness: [
        "fitness-home-hero",
        "fitness-home-programs",
        "fitness-home-trainers",
        "fitness-home-transformations",
        "fitness-home-cta"
    ],

    beauty: [
        "beauty-home-hero",
        "beauty-home-services",
        "beauty-home-specialists",
        "beauty-home-gallery",
        "beauty-home-cta"
    ]
};

function getDefaultVariants(verticalKey) {
    const keys = variantConfigs[verticalKey] || [];
    return keys.reduce((acc, key) => {
        acc[key] = 2;
        return acc;
    }, {});
}

const QA_FLOW_CARDS = [
    {
        key: "v2-preview-qa",
        title: "V2 Preview QA",
        badge: "Recommended",
        description: "Generate a V2 preview, inspect pages, images, mobile, links, and blueprint output.",
        steps: ["Choose vertical", "Run V2 preview", "Review QA", "Open pages", "Claim if needed"]
    },
    {
        key: "project-crm-qa",
        title: "CRM Redeploy QA",
        badge: "After claim",
        description: "Test whether CRM module edits are reflected in generated pages after redeploy.",
        steps: ["Pick project", "Pick module", "Run CRM QA", "Compare home/target page"]
    },
    {
        key: "public-preview-qa",
        title: "V1/Public Preview QA",
        badge: "Legacy",
        description: "Validate old public preview flow when comparing V1 vs V2 behavior.",
        steps: ["Choose sample", "Run preview", "Check legacy QA"]
    },
    {
        key: "renderer",
        title: "Renderer Only",
        badge: "Developer",
        description: "Render local/static payload directly without full public preview pipeline.",
        steps: ["Choose sample", "Edit JSON", "Render page"]
    },
    {
        key: "v2-visual-preview",
        title: "V2 Visual Preview",
        badge: "Fast",
        description: "Run real V2 pipeline and visually inspect generated pages without saving project or deploying.",
        steps: ["Choose scenario", "Generate HTML", "Switch pages", "Open preview"]
    }
];

const V2_QA_GUIDE = {
    commerce: {
        title: "Ecommerce",
        expectedPages: ["Home", "Products", "Collections", "About", "Contact", "Product details"],
        expectedModules: ["products", "collections", "about", "contact"],
        leadSource: "Product enquiry",
        risk: "Jewellery/store prompt should stay ecommerce and not become generic service."
    },
    saas: {
        title: "SaaS",
        expectedPages: ["Home", "Features", "Pricing", "Integrations", "About", "Contact"],
        expectedModules: ["features", "pricing", "integrations", "about", "contact"],
        leadSource: "Book demo",
        risk: "Should render SaaS product pages, not service/company pages."
    },
    academy: {
        title: "Academy",
        expectedPages: ["Home", "Programs", "Coaches", "Admissions", "About", "Contact", "Program details"],
        expectedModules: ["programs", "coaches", "about", "contact"],
        leadSource: "Program enquiry",
        risk: "Sports academy should not drift into legal/healthcare."
    },
    healthcare: {
        title: "Healthcare",
        expectedPages: ["Home", "Treatments", "Doctors", "About", "Contact", "Treatment details"],
        expectedModules: ["treatments", "doctors", "about", "contact"],
        leadSource: "Appointment",
        risk: "Clinic should render patient-first appointment flow."
    },
    legal: {
        title: "Legal",
        expectedPages: ["Home", "Practice Areas", "Team", "About", "Contact", "Practice detail"],
        expectedModules: ["practice-areas", "team", "about", "contact"],
        leadSource: "Legal consultation",
        risk: "Law firm should not mix academy/healthcare sections."
    },
    restaurant: {
        title: "Restaurant",
        expectedPages: ["Home", "Menu", "Specials", "Gallery", "About", "Contact", "Menu detail"],
        expectedModules: ["menuItems", "specials", "gallery", "about", "contact"],
        leadSource: "Reservation/menu enquiry",
        risk: "Restaurant should show food/menu flow, not ecommerce product store."
    },
    fitness: {
        title: "Fitness",
        expectedPages: ["Home", "Programs", "Trainers", "Membership", "About", "Contact"],
        expectedModules: ["programs", "trainers", "memberships", "about", "contact"],
        leadSource: "Trial booking",
        risk: "Book appointment must not convert fitness into healthcare."
    },
    beauty: {
        title: "Beauty",
        expectedPages: ["Home", "Services", "Specialists", "Gallery", "About", "Contact"],
        expectedModules: ["services", "specialists", "gallery", "about", "contact"],
        leadSource: "Appointment",
        risk: "Salon/spa should stay beauty, not healthcare."
    },
    realEstate: {
        title: "Real Estate Company",
        expectedPages: ["Home", "Properties", "Services", "About", "Contact", "Property details"],
        expectedModules: ["properties", "services", "about", "contact"],
        leadSource: "Property enquiry",
        risk: "Company/advisory should not become portal marketplace."
    },
    realEstatePortal: {
        title: "Real Estate Portal",
        expectedPages: ["Home", "Listings", "Buy", "Rent", "Agents", "Contact", "Listing details"],
        expectedModules: ["listings", "agents", "about", "contact"],
        leadSource: "Listing enquiry",
        risk: "Portal must win before real estate company when marketplace/listing words exist."
    }
};

function getV2VisualScenarioKey(selectedSample) {
    const map = {
        realEstatePortal: "real-estate-portal",
        realEstate: "real-estate-company",
        fitness: "fitness",
        beauty: "beauty"
    };

    return map[selectedSample] || "beauty";
}

export default function DevPlaygroundPage() {
    const [selectedSample, setSelectedSample] = useState("academy");
    const [jsonText, setJsonText] = useState(
        JSON.stringify(
            {
                ...samples.academy.payload,
                debugVariants: getDefaultVariants("academy")
            },
            null,
            2
        )
    );

    const [html, setHtml] = useState("");
    const [htmlText, setHtmlText] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [previewPage, setPreviewPage] = useState("home");

    const [flowMode, setFlowMode] = useState("renderer");
    const [viewMode, setViewMode] = useState("html");
    const [qaReport, setQaReport] = useState(null);
    const [previewSessionId, setPreviewSessionId] = useState("");
    const [cssText, setCssText] = useState("");
    const [jsText, setJsText] = useState("");
    const [lastGeneratedPages, setLastGeneratedPages] = useState([]);

    const [backendQaReport, setBackendQaReport] = useState(null);
    const [qaStatusText, setQaStatusText] = useState("");

    const [projectQaId, setProjectQaId] = useState("");
    const [projectQaModuleKey, setProjectQaModuleKey] = useState("products");
    const [projectCrmQaReport, setProjectCrmQaReport] = useState(null);

    const [projectOptions, setProjectOptions] = useState([]);
    const [projectOptionsLoading, setProjectOptionsLoading] = useState(false);

    const [claimAccountId, setClaimAccountId] = useState("");
    const [claimedProjectId, setClaimedProjectId] = useState("");

    const loadProjectOptions = async () => {
        try {
            setProjectOptionsLoading(true);
            setError("");

            const result = await getDevDiagnosticProjects(100);
            setProjectOptions(result.projects || result.Projects || []);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data ||
                err.message ||
                "Failed to load projects."
            );
        } finally {
            setProjectOptionsLoading(false);
        }
    };

    useEffect(() => {
        if (flowMode === "project-crm-qa" && projectOptions.length === 0) {
            loadProjectOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flowMode]);

    const [availablePages, setAvailablePages] = useState(
        getDefaultPages("academy")
    );

    const handleClaimV2Preview = async () => {
        try {
            setLoading(true);
            setError("");

            if (!previewSessionId) {
                setError("Run V2 preview first.");
                return;
            }

            if (!claimAccountId) {
                setError("Account ID is required to claim V2 preview.");
                return;
            }

            const result = await claimV2PreviewQa(previewSessionId, claimAccountId);

            const projectId =
                result.projectId ||
                result.ProjectId ||
                "";

            setClaimedProjectId(projectId);

            setBackendQaReport((prev) => ({
                ...(prev || {}),
                claimResult: result
            }));

            setQaStatusText("V2 preview claimed successfully.");
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data ||
                err.message ||
                "Failed to claim V2 preview."
            );
        } finally {
            setLoading(false);
        }
    };

    function getDefaultPages(verticalKey) {
        const pages = {
            commerce: [
                { key: "home", title: "Home" },
                { key: "products", title: "Products" },
                { key: "collections", title: "Collections" }
            ],

            academy: [
                { key: "home", title: "Home" },
                { key: "programs", title: "Programs" },
                { key: "coaches", title: "Coaches" },
                { key: "admissions", title: "Admissions" },
                { key: "contact", title: "Contact" }
            ],

            realEstate: [
                { key: "home", title: "Home" },
                { key: "properties", title: "Properties" },
                { key: "services", title: "Services" },
                { key: "about", title: "About" },
                { key: "contact", title: "Contact" }
            ],

            realEstatePortal: [
                { key: "home", title: "Home" },
                { key: "listings", title: "Listings" },
                { key: "buy", title: "Buy" },
                { key: "rent", title: "Rent" },
                { key: "agents", title: "Agents" },
                { key: "contact", title: "Contact" }
            ],

            saas: [
                { key: "home", title: "Home" },
                { key: "features", title: "Features" },
                { key: "pricing", title: "Pricing" },
                { key: "integrations", title: "Integrations" },
                { key: "contact", title: "Contact" }
            ],

            legal: [
                { key: "home", title: "Home" },
                { key: "practice-areas", title: "Practice Areas" },
                { key: "team", title: "Legal Team" },
                { key: "contact", title: "Contact" }
            ],

            healthcare: [
                { key: "home", title: "Home" },
                { key: "treatments", title: "Treatments" },
                { key: "doctors", title: "Doctors" },
                { key: "contact", title: "Contact" }
            ],

            restaurant: [
                { key: "home", title: "Home" },
                { key: "menu", title: "Menu" },
                { key: "specials", title: "Specials" },
                { key: "gallery", title: "Gallery" },
                { key: "contact", title: "Contact" }
            ],

            fitness: [
                { key: "home", title: "Home" },
                { key: "programs", title: "Programs" },
                { key: "trainers", title: "Trainers" },
                { key: "membership", title: "Membership" },
                { key: "contact", title: "Contact" }
            ],

            beauty: [
                { key: "home", title: "Home" },
                { key: "services", title: "Services" },
                { key: "specialists", title: "Specialists" },
                { key: "gallery", title: "Gallery" },
                { key: "contact", title: "Contact" }
            ]
        };

        return pages[verticalKey] || [{ key: "home", title: "Home" }];
    }

    const handleSampleChange = (key) => {
        const payload = {
            ...samples[key].payload,
            debugVariants: getDefaultVariants(key)
        };

        setSelectedSample(key);
        setPreviewPage("home");
        setAvailablePages(getDefaultPages(key));
        setJsonText(JSON.stringify(payload, null, 2));
        setHtml("");
        setHtmlText("");
        setError("");
        setQaReport(null);
        setBackendQaReport(null);
        setPreviewSessionId("");
        setCssText("");
        setJsText("");
        setQaStatusText("");
        setViewMode("html");
    };

    const openPreviewInNewTab = (rawHtml, options = {}) => {
        if (!rawHtml) return;

        const css = options.cssText || cssText || "";
        const js = options.jsText || "";

        let finalHtml = rawHtml;

        if (css && !finalHtml.includes("data-qa-inline-css")) {
            finalHtml = finalHtml.replace(
                /<\/head>/i,
                `<style data-qa-inline-css>\n${css}\n</style>\n</head>`
            );
        }

        if (js && !finalHtml.includes("data-qa-inline-js")) {
            finalHtml = finalHtml.replace(
                /<\/body>/i,
                `<script data-qa-inline-js>\n${js}\n</script>\n</body>`
            );
        }

        const blob = new Blob([finalHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        window.open(url, "_blank", "noopener,noreferrer");

        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    };

    const copyGeneratedHtml = async () => {
        try {
            await navigator.clipboard.writeText(htmlText || html || "");
            alert("Generated HTML copied!");
        } catch (error) {
            console.error(error);
            alert("Copy failed.");
        }
    };

    const getPublicPreviewDefaults = (verticalKey) => {
        const defaults = {
            academy: {
                businessModel: "academy",
                primaryWebsiteMode: "program-first",
                primaryEntityType: "programs",
                locationFocus: "local",
                contentStyleHint: "academy-program-first",
                pages: ["Home", "Programs", "Coaches", "Admissions", "Contact"]
            },

            fitness: {
                verticalKey: "fitness",
                templatePackKey: "fitness",
                businessModel: "service",
                primaryWebsiteMode: "program-first",
                primaryEntityType: "fitness-programs",
                locationFocus: "local",
                contentStyleHint: "fitness",
                pages: ["Home", "Programs", "Trainers", "Membership", "About", "Contact"]
            },

            commerce: {
                businessModel: "ecommerce",
                primaryWebsiteMode: "product-first",
                primaryEntityType: "products",
                locationFocus: "national",
                contentStyleHint: "commerce-product-first",
                pages: ["Home", "Products", "Collections", "Contact"]
            },

            realEstate: {
                verticalKey: "realestate-company",
                templatePackKey: "realestate-company",
                businessModel: "service",
                primaryWebsiteMode: "advisory-first",
                primaryEntityType: "properties",
                locationFocus: "local",
                contentStyleHint: "realestate-company",
                pages: ["Home", "Properties", "Services", "About", "Contact"]
            },

            realEstatePortal: {
                verticalKey: "realestate-portal",
                templatePackKey: "realestate-portal",
                businessModel: "marketplace",
                primaryWebsiteMode: "search-first",
                primaryEntityType: "listings",
                locationFocus: "local",
                contentStyleHint: "realestate-portal",
                pages: ["Home", "Listings", "Buy", "Rent", "Agents", "Contact"]
            },

            healthcare: {
                businessModel: "service",
                primaryWebsiteMode: "appointment-first",
                primaryEntityType: "treatments",
                locationFocus: "local",
                contentStyleHint: "healthcare-clinic",
                pages: ["Home", "Treatments", "Doctors", "Contact"]
            },

            legal: {
                businessModel: "service",
                primaryWebsiteMode: "consultation-first",
                primaryEntityType: "practice-areas",
                locationFocus: "local",
                contentStyleHint: "legal-trust-first",
                pages: ["Home", "Practice Areas", "Team", "Contact"]
            },

            restaurant: {
                businessModel: "service",
                primaryWebsiteMode: "menu-first",
                primaryEntityType: "menu-items",
                locationFocus: "local",
                contentStyleHint: "restaurant-menu-first",
                pages: ["Home", "Menu", "Specials", "Gallery", "Contact"]
            },

            beauty: {
                businessModel: "service",
                primaryWebsiteMode: "booking-first",
                primaryEntityType: "services",
                locationFocus: "local",
                contentStyleHint: "beauty-services-first",
                pages: ["Home", "Services", "Specialists", "Gallery", "Contact"]
            },

            saas: {
                businessModel: "saas",
                primaryWebsiteMode: "conversion-first",
                primaryEntityType: "features",
                locationFocus: "global",
                contentStyleHint: "saas-conversion",
                pages: ["Home", "Features", "Pricing", "Integrations", "Contact"]
            }
        };

        return defaults[verticalKey] || {
            businessModel: "service",
            primaryWebsiteMode: "service-first",
            primaryEntityType: "services",
            locationFocus: "local",
            contentStyleHint: "service-business",
            pages: ["Home", "Services", "Contact"]
        };
    };

    const toPublicPreviewRequest = (payload) => {
        const defaults = getPublicPreviewDefaults(selectedSample);
        const samplePages = defaults.pages || getDefaultPages(selectedSample).map((x) => x.title || x.key);

        return {
            businessName: payload.businessName,
            industry: payload.industry,
            subIndustry: payload.subIndustry,

            description:
                payload.description ||
                payload.businessDescription ||
                payload.data?.hero?.subtitle ||
                payload.data?.brand?.tagline ||
                `${payload.businessName} is a ${payload.subIndustry || payload.industry} business.`,

            tone: payload.toneOfVoice || payload.tone || "Professional",
            color: payload.color || "Blue",
            themeKey: payload.themeKey || payload.data?.meta?.themeKey || "corporate-clean",

            brandStyle: payload.brandStyle || "Professional",
            goalOfWebsite: payload.primaryGoal || payload.goalOfWebsite || "Generate Leads",
            callToActionType:
                payload.primaryCallToAction ||
                payload.callToActionType ||
                "Contact Us",

            businessModel: payload.businessModel || defaults.businessModel,
            primaryWebsiteMode: payload.primaryWebsiteMode || defaults.primaryWebsiteMode,
            primaryEntityType: payload.primaryEntityType || defaults.primaryEntityType,
            locationFocus: payload.locationFocus || defaults.locationFocus,
            contentStyleHint: payload.contentStyleHint || defaults.contentStyleHint,

            verticalKey: payload.verticalKey || defaults.verticalKey || selectedSample,
            templatePackKey: payload.templatePackKey || defaults.templatePackKey || defaults.contentStyleHint,

            desiredLayoutStyle: payload.desiredLayoutStyle || "",
            visualTone: payload.visualTone || "",
            preferredSectionDensity: payload.preferredSectionDensity || "",

            location: payload.location || "Ahmedabad",
            pages: samplePages,

            offeredItems:
                payload.offeredItems ||
                payload.data?.ShowcaseItems?.map((item) => ({
                    name: item.Title || item.title,
                    subtitle: item.Subtitle || item.subtitle,
                    badge: item.Badge || item.badge,
                    category: item.Category || item.category,
                    keyPoints: item.Meta || item.Tags || []
                })) ||
                []
        };
    };

    const normalizePreviewResponse = (result) => {
        if (typeof result === "string") {
            return {
                sessionId: previewSessionId || "",
                html: result,
                pages: lastGeneratedPages || [],
                css: "",
                js: "",
                raw: result
            };
        }

        const data = result?.data || result || {};

        const sessionId =
            data.previewSessionId ||
            data.PreviewSessionId ||
            data.sessionId ||
            data.SessionId ||
            data.id ||
            data.Id ||
            data.previewId ||
            data.PreviewId ||
            "";

        const renderedHtml =
            data.html ||
            data.Html ||
            data.previewHtml ||
            data.PreviewHtml ||
            data.previewDocument ||
            data.PreviewDocument ||
            data.documentHtml ||
            data.DocumentHtml ||
            "";

        const pages =
            data.pages ||
            data.Pages ||
            data.generatedPages ||
            data.GeneratedPages ||
            data.recommendedPages ||
            data.RecommendedPages ||
            data.previewPages ||
            data.PreviewPages ||
            [];

        const css =
            data.css ||
            data.Css ||
            data.siteCss ||
            data.SiteCss ||
            data.cssText ||
            data.CssText ||
            data.assets?.["assets/site.css"] ||
            data.Assets?.["assets/site.css"] ||
            "";

        const js =
            data.js ||
            data.Js ||
            data.siteJs ||
            data.SiteJs ||
            data.jsText ||
            data.JsText ||
            data.assets?.["assets/site.js"] ||
            data.Assets?.["assets/site.js"] ||
            "";

        return {
            sessionId,
            html: renderedHtml,
            pages,
            css,
            js,
            raw: data
        };
    };

    const handleRender = async () => {
        try {
            setLoading(true);
            setError("");
            setQaReport(null);
            setBackendQaReport(null);
            setQaStatusText("");

            const parsed = JSON.parse(jsonText);

            if (flowMode === "v2-preview-qa") {
                const request = toPublicPreviewRequest(parsed);

                setQaStatusText("Running V2 blueprint preview pipeline...");

                const queued = await generateV2PreviewQa(request);

                const queuedSessionId =
                    queued.previewSessionId ||
                    queued.PreviewSessionId ||
                    queued.sessionId ||
                    queued.id ||
                    "";

                if (!queuedSessionId) {
                    throw new Error("V2 preview was generated but no previewSessionId was returned.");
                }

                setPreviewSessionId(queuedSessionId);

                setQaStatusText("Loading V2 preview session...");

                const previewResult = await getV2PreviewQa(queuedSessionId);

                const generatedPagesJson =
                    previewResult.generatedPagesJson ||
                    previewResult.GeneratedPagesJson ||
                    "";

                let parsedBundle = null;
                try {
                    parsedBundle = generatedPagesJson ? JSON.parse(generatedPagesJson) : null;
                } catch {
                    parsedBundle = null;
                }

                const rawPages =
                    parsedBundle?.pages ||
                    parsedBundle?.Pages ||
                    previewResult.pages ||
                    previewResult.Pages ||
                    [];

                const generatedPages = normalizeGeneratedPages(rawPages);

                let renderedHtml = "";
                try {
                    renderedHtml = await getV2PreviewQaDocument(queuedSessionId, "home");
                } catch {
                    renderedHtml =
                        previewResult.previewHtml ||
                        previewResult.PreviewHtml ||
                        "";
                }

                let css = "";
                let js = "";

                try {
                    css = await getV2PreviewQaAsset(queuedSessionId, "site.css");
                } catch {
                    css = "";
                }

                try {
                    js = await getV2PreviewQaAsset(queuedSessionId, "site.js");
                } catch {
                    js = "";
                }

                const blueprintJson =
                    previewResult.blueprintJson ||
                    previewResult.BlueprintJson ||
                    "";

                const blueprintQaJson =
                    previewResult.blueprintQaJson ||
                    previewResult.BlueprintQaJson ||
                    "";

                let blueprint = null;
                let blueprintQa = null;

                try {
                    blueprint = blueprintJson ? JSON.parse(blueprintJson) : null;
                } catch {
                    blueprint = null;
                }

                try {
                    blueprintQa = blueprintQaJson ? JSON.parse(blueprintQaJson) : null;
                } catch {
                    blueprintQa = null;
                }

                const nextPages =
                    generatedPages.length > 0
                        ? generatedPages.map((page) => ({
                            key: page.key,
                            title: page.title || page.key
                        }))
                        : getDefaultPages(selectedSample);

                setAvailablePages(nextPages);
                setPreviewPage(nextPages[0]?.key || "home");
                setLastGeneratedPages(generatedPages);

                setCssText(css || "");
                setJsText(js || "");
                setHtml(renderedHtml || "");
                setHtmlText(renderedHtml || "");

                const report = runGenerationQa({
                    verticalKey: selectedSample,
                    html: renderedHtml || "",
                    cssText: css || "",
                    jsText: js || "",
                    generatedPages
                });

                setQaReport(report);

                setBackendQaReport({
                    qaStatus: blueprintQa?.errors?.length ? "warning" : "passed",
                    errors: blueprintQa?.errors || [],
                    warnings: [],
                    generatedPages: blueprintQa?.pages || generatedPages,
                    blueprint,
                    blueprintQa,
                    v2: true
                });

                setViewMode("qa");
                setQaStatusText("V2 Blueprint Preview QA completed.");

                if (renderedHtml) {
                    openPreviewInNewTab(renderedHtml, {
                        cssText: css,
                        jsText: js
                    });
                }

                return;
            }

            if (flowMode === "public-preview-qa") {
                const request = toPublicPreviewRequest(parsed);

                setQaStatusText("Creating public preview session...");

                const queued = await generatePublicPreviewQa(request);
                const queuedSessionId =
                    queued.previewSessionId ||
                    queued.PreviewSessionId ||
                    queued.sessionId ||
                    queued.id ||
                    "";

                if (!queuedSessionId) {
                    throw new Error("Preview session was created but no previewSessionId was returned.");
                }

                setPreviewSessionId(queuedSessionId);

                setQaStatusText("Waiting for public preview generation...");

                await waitForPublicPreviewQa(queuedSessionId, {
                    maxAttempts: 45,
                    delayMs: 2000,
                    onTick: ({ attempt, maxAttempts, status }) => {
                        setQaStatusText(
                            `Waiting for public preview generation... ${attempt}/${maxAttempts} (${status || "queued"})`
                        );
                    }
                });

                setQaStatusText("Loading generated preview session...");

                const previewResult = await getPublicPreviewQa(queuedSessionId);
                const normalized = normalizePreviewResponse(previewResult);

                const generatedPages = normalizeGeneratedPages(normalized.pages);

                let css = normalized.css || "";
                let js = normalized.js || "";

                try {
                    css = css || await getPublicPreviewQaAsset(queuedSessionId, "site.css");
                } catch {
                    // Some previews may not have CSS asset.
                }

                try {
                    js = js || await getPublicPreviewQaAsset(queuedSessionId, "site.js");
                } catch {
                    // Some previews may not have JS asset.
                }

                let backendQa = null;
                try {
                    backendQa = await getPublicPreviewQaDiagnostics(queuedSessionId);
                } catch {
                    // Backend QA is optional; frontend QA still runs.
                }

                setBackendQaReport(backendQa);
                setLastGeneratedPages(generatedPages);
                setCssText(css || "");
                setJsText(js || "");

                const nextPages =
                    generatedPages.length > 0
                        ? generatedPages.map((page) => ({
                            key: page.key,
                            title: page.title || page.key
                        }))
                        : getDefaultPages(selectedSample);

                setAvailablePages(nextPages);
                setPreviewPage(nextPages[0]?.key || "home");

                setHtml(normalized.html || "");
                setHtmlText(normalized.html || "");

                const report = runGenerationQa({
                    verticalKey: selectedSample,
                    html: normalized.html || "",
                    cssText: css || "",
                    jsText: js || "",
                    generatedPages
                });

                setQaReport(report);
                setViewMode("qa");
                setQaStatusText("Public Preview QA completed.");

                if (normalized.html) {
                    openPreviewInNewTab(normalized.html, {
                        cssText: css,
                        jsText: js
                    });
                }

                return;
            }

            if (flowMode === "v2-visual-preview") {
                setQaStatusText("Generating V2 visual preview...");

                const result = await generateV2VisualPreview({
                    scenarioKey: getV2VisualScenarioKey(selectedSample),
                    pageKey: previewPage || "home"
                });

                const renderedHtml = result.html || result.Html || "";
                const pages = result.pages || result.Pages || [];

                const normalizedPages = pages.map((page) => ({
                    key: page.key || page.Key,
                    title: page.title || page.Title || page.key || page.Key
                }));

                setAvailablePages(
                    normalizedPages.length > 0
                        ? normalizedPages
                        : getDefaultPages(selectedSample)
                );

                setPreviewPage(
                    result.selectedPageKey ||
                    result.SelectedPageKey ||
                    normalizedPages[0]?.key ||
                    "home"
                );

                setHtml(renderedHtml);
                setHtmlText(renderedHtml);
                setCssText("");
                setJsText("");
                setLastGeneratedPages(normalizedPages);

                setViewMode("html");
                setQaStatusText("V2 visual preview generated.");

                if (renderedHtml) {
                    openPreviewInNewTab(renderedHtml);
                }

                return;
            }

            const payload = {
                ...parsed,
                previewPage
            };

            const result = await renderDevPreview(payload);
            const renderedHtml = result.html || "";

            const pages = result.pages || availablePages;

            setAvailablePages(pages);
            setHtml(renderedHtml);
            setHtmlText(renderedHtml);
            setCssText(result.css || result.siteCss || "");
            setJsText(result.js || result.siteJs || "");

            const report = runGenerationQa({
                verticalKey: selectedSample,
                html: renderedHtml,
                cssText: result.css || result.siteCss || "",
                jsText: result.js || result.siteJs || "",
                generatedPages: pages
            });

            setQaReport(report);
            setViewMode("html");

            openPreviewInNewTab(renderedHtml, {
                cssText,
                jsText
            });
        } catch (err) {
            console.error(err);

            const apiError =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data ||
                err.message ||
                "Failed to render preview.";

            setError(apiError);
        } finally {
            setLoading(false);
        }
    };

    const handleRenderSelectedPublicPage = async () => {
        if (!previewSessionId) {
            setError("No preview session found. Generate public preview first.");
            return;
        }

        if (flowMode === "v2-visual-preview") {
            try {
                setLoading(true);
                setError("");
                setQaStatusText(`Rendering V2 visual page: ${previewPage}`);

                const result = await generateV2VisualPreview({
                    scenarioKey: getV2VisualScenarioKey(selectedSample),
                    pageKey: previewPage || "home"
                });

                const renderedHtml = result.html || result.Html || "";

                setHtml(renderedHtml);
                setHtmlText(renderedHtml);
                setViewMode("html");
                setQaStatusText(`Rendered V2 visual page: ${previewPage}`);

                if (renderedHtml) {
                    openPreviewInNewTab(renderedHtml);
                }

                return;
            } catch (err) {
                console.error(err);
                setError(
                    err?.response?.data?.message ||
                    err?.response?.data?.error ||
                    err?.response?.data ||
                    err.message ||
                    "Failed to render V2 visual preview page."
                );
            } finally {
                setLoading(false);
            }

            return;
        }

        try {
            setLoading(true);
            setError("");
            setQaStatusText(`Rendering selected page: ${previewPage}`);

            const result =
                flowMode === "v2-preview-qa"
                    ? await getV2PreviewQaDocument(previewSessionId, previewPage)
                    : await getPublicPreviewQaDocument(previewSessionId, previewPage);
            const normalized = normalizePreviewResponse(result);

            const renderedHtml = normalized.html || "";

            setHtml(renderedHtml);
            setHtmlText(renderedHtml);

            const report = runGenerationQa({
                verticalKey: selectedSample,
                html: renderedHtml,
                cssText,
                jsText,
                generatedPages: lastGeneratedPages
            });

            setQaReport(report);
            setViewMode("html");

            if (renderedHtml) {
                openPreviewInNewTab(renderedHtml, {
                    cssText,
                    jsText
                });
            } else {
                setError("Selected page rendered but no HTML was returned.");
            }

            setQaStatusText(`Rendered selected page: ${previewPage}`);
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data ||
                err.message ||
                "Failed to render selected preview page."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleV2Compare = async () => {
        try {
            setLoading(true);
            setError("");
            setQaStatusText("Inspecting V2 pipeline...");

            const parsed = JSON.parse(jsonText);
            const request = toPublicPreviewRequest(parsed);

            const result = await compareV2PreviewQa(request);

            setBackendQaReport({
                qaStatus: result?.v2?.qa?.length ? "warning" : "passed",
                errors: result?.v2?.qa || [],
                warnings: result?.notes || [],
                generatedPages: result?.v2?.pages || [],
                blueprint: {
                    blueprint: {
                        verticalKey: result?.v2?.verticalKey
                    },
                    intent: result?.v2?.intent
                },
                pipeline: result,
                v2: true
            });

            const pages = normalizeGeneratedPages(result?.v2?.pages || []);
            setLastGeneratedPages(pages);
            setAvailablePages(
                pages.length
                    ? pages.map((page) => ({
                        key: page.key,
                        title: page.title || page.key
                    }))
                    : getDefaultPages(selectedSample)
            );

            setViewMode("qa");
            setQaStatusText("V2 pipeline inspection completed.");
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data ||
                err.message ||
                "Failed to inspect V2 pipeline."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleRunProjectCrmQa = async () => {
        if (!projectQaId.trim()) {
            setError("Project ID is required for Project CRM QA.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            setQaStatusText("Running Project CRM Render QA...");
            setProjectCrmQaReport(null);

            const result = await getProjectCrmRenderQa(
                projectQaId.trim(),
                projectQaModuleKey
            );

            setProjectCrmQaReport(result);

            setCssText(result.cssText || "");
            setJsText(result.jsText || "");

            setViewMode("project-crm-qa");
            setQaStatusText("Project CRM Render QA completed.");

            if (result.targetPageHtml) {
                openPreviewInNewTab(result.targetPageHtml, {
                    cssText: result.cssText || result.css || "",
                    jsText: result.jsText || result.js || ""
                });
            } else if (result.previewHtml) {
                openPreviewInNewTab(result.previewHtml);
            }
        } catch (err) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.response?.data ||
                err.message ||
                "Failed to run Project CRM QA."
            );
        } finally {
            setLoading(false);
        }
    };

    const renderQaReport = () => {
        if (!qaReport && !backendQaReport) {
            return (
                <div style={styles.empty}>
                    Generate a preview to see QA results.
                </div>
            );
        }

        const activeStatus =
            backendQaReport?.qaStatus ||
            backendQaReport?.QaStatus ||
            qaReport?.status ||
            "unknown";

        const normalizedStatus = String(activeStatus).toLowerCase();

        const statusStyle =
            normalizedStatus === "failed"
                ? styles.statusFailed
                : normalizedStatus === "warning"
                    ? styles.statusWarning
                    : styles.statusPassed;

        const backendErrors = backendQaReport?.errors || backendQaReport?.Errors || [];
        const backendWarnings = backendQaReport?.warnings || backendQaReport?.Warnings || [];
        const backendGeneratedPages = backendQaReport?.generatedPages || backendQaReport?.GeneratedPages || [];
        const backendBrokenLinks = backendQaReport?.brokenNavbarLinks || backendQaReport?.BrokenNavbarLinks || [];
        const backendMissingPages = backendQaReport?.missingExpectedPages || backendQaReport?.MissingExpectedPages || [];

        return (
            <div style={styles.qaReport}>
                <div style={{ ...styles.statusBadge, ...statusStyle }}>
                    QA Status: {String(activeStatus).toUpperCase()}
                </div>

                {previewSessionId && (
                    <div style={styles.qaMeta}>
                        Preview Session: <strong>{previewSessionId}</strong>
                    </div>
                )}

                {backendQaReport && (
                    <div style={styles.qaSection}>
                        <h4>Backend QA Summary</h4>
                        <div>Generated pages: {backendGeneratedPages.length}</div>
                        <div>Missing expected pages: {backendMissingPages.length}</div>
                        <div>Broken navbar links: {backendBrokenLinks.length}</div>
                        <div>
                            CSS classes:{" "}
                            {backendQaReport.usedClassCount ?? backendQaReport.UsedClassCount ?? 0} used /{" "}
                            {backendQaReport.definedCssClassCount ?? backendQaReport.DefinedCssClassCount ?? 0} defined
                        </div>
                        <div>
                            Mobile:{" "}
                            {(backendQaReport.hasViewportMeta ?? backendQaReport.HasViewportMeta) ? "Viewport ✅" : "Viewport ❌"}{" "}
                            {(backendQaReport.hasMediaQuery ?? backendQaReport.HasMediaQuery) ? "Media ✅" : "Media ⚠️"}{" "}
                            {(backendQaReport.hasMobileNavMarker ?? backendQaReport.HasMobileNavMarker) ? "Nav ✅" : "Nav ⚠️"}
                        </div>
                    </div>
                )}

                <div style={styles.qaSection}>
                    <h4>Errors</h4>
                    {backendErrors.length ? (
                        backendErrors.map((item, index) => (
                            <div key={`be-${index}`} style={styles.qaError}>❌ {item}</div>
                        ))
                    ) : qaReport?.errors?.length ? (
                        qaReport.errors.map((item, index) => (
                            <div key={`fe-${index}`} style={styles.qaError}>❌ {item}</div>
                        ))
                    ) : (
                        <div style={styles.qaOk}>✅ No blocking errors found.</div>
                    )}
                </div>

                <div style={styles.qaSection}>
                    <h4>Warnings</h4>
                    {backendWarnings.length ? (
                        backendWarnings.map((item, index) => (
                            <div key={`bw-${index}`} style={styles.qaWarning}>⚠️ {item}</div>
                        ))
                    ) : qaReport?.warnings?.length ? (
                        qaReport.warnings.map((item, index) => (
                            <div key={`fw-${index}`} style={styles.qaWarning}>⚠️ {item}</div>
                        ))
                    ) : (
                        <div style={styles.qaOk}>✅ No warnings found.</div>
                    )}
                </div>

                {qaReport && (
                    <div style={styles.qaGrid}>
                        <div style={styles.qaBox}>
                            <h4>Expected Pages</h4>
                            {qaReport.expectedPages.map((page) => (
                                <div key={page}>
                                    {qaReport.missingExpectedPages.includes(page) ? "❌" : "✅"}{" "}
                                    {page} → {page === "home" ? "index.html" : `${page}.html`}
                                </div>
                            ))}
                        </div>

                        <div style={styles.qaBox}>
                            <h4>Generated Pages</h4>
                            {qaReport.generatedPages.length ? (
                                qaReport.generatedPages.map((page) => (
                                    <div key={page.key}>
                                        ✅ {page.title} → {page.fileName}
                                    </div>
                                ))
                            ) : (
                                <div>❌ No generated pages returned.</div>
                            )}
                        </div>

                        <div style={styles.qaBox}>
                            <h4>Navbar Links</h4>
                            {qaReport.navLinks.length ? (
                                qaReport.navLinks.map((link) => {
                                    const broken = qaReport.brokenNavbarLinks.some(
                                        (x) => x.href === link.href
                                    );

                                    return (
                                        <div key={link.href}>
                                            {broken ? "❌" : "✅"} {link.href}
                                        </div>
                                    );
                                })
                            ) : (
                                <div>⚠️ No internal navbar/page links found.</div>
                            )}
                        </div>

                        <div style={styles.qaBox}>
                            <h4>CSS / Mobile</h4>
                            <div>{qaReport.hasViewportMeta ? "✅" : "❌"} Viewport meta</div>
                            <div>{qaReport.hasMediaQuery ? "✅" : "⚠️"} Media queries</div>
                            <div>{qaReport.hasHamburger ? "✅" : "⚠️"} Mobile nav marker</div>
                            <div>Used classes: {qaReport.usedClasses.length}</div>
                            <div>Defined CSS classes: {qaReport.definedClasses.length}</div>
                            <div>Missing CSS classes: {qaReport.missingCssClasses.length}</div>
                        </div>

                        <div style={styles.qaBox}>
                            <h4>Assets</h4>
                            <div>CSS links: {qaReport.cssLinks.length}</div>
                            {qaReport.cssLinks.map((x) => (
                                <div key={x}>• {x}</div>
                            ))}
                            <div style={{ marginTop: 8 }}>JS links: {qaReport.scriptLinks.length}</div>
                            {qaReport.scriptLinks.map((x) => (
                                <div key={x}>• {x}</div>
                            ))}
                        </div>

                        <div style={styles.qaBox}>
                            <h4>JS Handlers</h4>
                            <div>Called: {qaReport.calledFunctions.length}</div>
                            <div>Defined: {qaReport.definedFunctions.length}</div>
                            <div>Missing: {qaReport.missingJsHandlers.length}</div>
                            {qaReport.missingJsHandlers.map((x) => (
                                <div key={x}>❌ {x}</div>
                            ))}
                        </div>
                    </div>
                )}

                {backendQaReport?.v2 && (
                    <div style={styles.qaSection}>
                        <h4>V2 Blueprint</h4>
                        <div>
                            Vertical:{" "}
                            <strong>
                                {backendQaReport?.blueprint?.blueprint?.verticalKey ||
                                    backendQaReport?.blueprint?.Blueprint?.VerticalKey ||
                                    backendQaReport?.pipeline?.v2?.verticalKey ||
                                    "unknown"}
                            </strong>
                        </div>
                        <div>
                            Intent:{" "}
                            <strong>
                                {backendQaReport?.blueprint?.intent?.profileLabel ||
                                    backendQaReport?.blueprint?.Intent?.ProfileLabel ||
                                    backendQaReport?.pipeline?.v2?.intent?.profileLabel ||
                                    "default"}
                            </strong>
                        </div>
                        <div>
                            Template:{" "}
                            <strong>
                                {backendQaReport?.pipeline?.v2?.templatePackKey || "unknown"}
                            </strong>
                        </div>
                        <div>
                            Entity:{" "}
                            <strong>
                                {backendQaReport?.pipeline?.v2?.primaryEntityType || "unknown"}
                            </strong>
                        </div>
                    </div>
                )}

                {qaReport?.missingCssClasses?.length > 0 && (
                    <div style={styles.qaSection}>
                        <h4>Missing CSS Classes</h4>
                        <div style={styles.codeList}>
                            {qaReport.missingCssClasses.slice(0, 120).join(", ")}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <PageShell title="V2 QA Playground">
            <div style={styles.page}>
                <div style={styles.heroPanel}>
                    <div>
                        <div style={styles.kicker}>Shriansh Nexus QA</div>
                        <h2 style={styles.title}>V2 Website Testing Playground</h2>
                        <p style={styles.subtitle}>
                            A guided testing screen for QA users to validate V2 vertical selection, generated pages,
                            images, mobile support, leads, CRM bootstrap, and redeploy output.
                        </p>
                    </div>

                    <div style={styles.heroActions}>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={copyGeneratedHtml}
                            disabled={!htmlText && !html}
                        >
                            Copy HTML
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleRender}
                            disabled={loading}
                        >
                            {loading ? "Running..." : flowMode === "v2-preview-qa" ? "Run V2 Preview QA" : "Run Selected Test"}
                        </button>
                    </div>
                </div>

                {error && <div style={styles.errorBanner}>❌ {String(error)}</div>}
                {qaStatusText && <div style={styles.infoBanner}>ℹ️ {qaStatusText}</div>}

                <div style={styles.flowGrid}>
                    {QA_FLOW_CARDS.map((item) => {
                        const active = flowMode === item.key;

                        return (
                            <button
                                key={item.key}
                                type="button"
                                onClick={() => {
                                    setFlowMode(item.key);
                                    setQaReport(null);
                                    setPreviewSessionId("");
                                    setViewMode("html");
                                }}
                                style={{
                                    ...styles.flowCard,
                                    ...(active ? styles.flowCardActive : {})
                                }}
                            >
                                <div style={styles.flowTop}>
                                    <strong>{item.title}</strong>
                                    <span style={active ? styles.badgeActive : styles.badge}>{item.badge}</span>
                                </div>
                                <p>{item.description}</p>
                                <div style={styles.stepPills}>
                                    {item.steps.map((step) => (
                                        <span key={step}>{step}</span>
                                    ))}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {flowMode === "v2-preview-qa" && (
                    <div style={styles.workspaceGrid}>
                        <div style={styles.leftPanel}>
                            <div style={styles.sectionHeader}>
                                <div>
                                    <h3>1. Select V2 vertical</h3>
                                    <p>Choose the vertical you want QA to test. This loads a safe sample payload.</p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleV2Compare}
                                    disabled={loading}
                                >
                                    Inspect V2 Pipeline
                                </button>
                            </div>

                            <div style={styles.verticalGrid}>
                                {Object.entries(samples).map(([key, sample]) => {
                                    const active = selectedSample === key;
                                    const guide = V2_QA_GUIDE[key];

                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => handleSampleChange(key)}
                                            style={{
                                                ...styles.verticalCard,
                                                ...(active ? styles.verticalCardActive : {})
                                            }}
                                        >
                                            <strong>{guide?.title || sample.label}</strong>
                                            <span>{sample.payload?.industry || "Business"}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            <div style={styles.guideCard}>
                                <h3>2. What QA should verify</h3>
                                <div style={styles.guideGrid}>
                                    <div>
                                        <strong>Expected pages</strong>
                                        {(V2_QA_GUIDE[selectedSample]?.expectedPages || []).map((x) => (
                                            <div key={x}>✅ {x}</div>
                                        ))}
                                    </div>
                                    <div>
                                        <strong>Expected CRM modules</strong>
                                        {(V2_QA_GUIDE[selectedSample]?.expectedModules || []).map((x) => (
                                            <div key={x}>✅ {x}</div>
                                        ))}
                                    </div>
                                    <div>
                                        <strong>Lead flow</strong>
                                        <div>✅ {V2_QA_GUIDE[selectedSample]?.leadSource || "Website enquiry"}</div>
                                        <strong style={{ display: "block", marginTop: 12 }}>Risk to watch</strong>
                                        <div>⚠️ {V2_QA_GUIDE[selectedSample]?.risk || "Check vertical drift."}</div>
                                    </div>
                                </div>
                            </div>

                            <div style={styles.controlsCard}>
                                <div style={styles.sectionHeader}>
                                    <div>
                                        <h3>3. Preview controls</h3>
                                        <p>Run preview first. Then open any generated page and review QA output.</p>
                                    </div>
                                </div>

                                <div style={styles.formGrid}>
                                    <label style={styles.field}>
                                        <span>Generated page</span>
                                        <select
                                            value={previewPage}
                                            onChange={(e) => setPreviewPage(e.target.value)}
                                            style={styles.select}
                                        >
                                            {availablePages.map((page) => (
                                                <option key={page.key} value={page.key}>
                                                    {page.title || page.key}
                                                </option>
                                            ))}
                                        </select>
                                    </label>

                                    <label style={styles.field}>
                                        <span>Claim account ID</span>
                                        <input
                                            value={claimAccountId}
                                            onChange={(e) => setClaimAccountId(e.target.value)}
                                            placeholder="Account ID for claim test"
                                            style={styles.input}
                                        />
                                    </label>
                                </div>

                                <div style={styles.actionsRow}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleRender}
                                        disabled={loading}
                                    >
                                        {loading ? "Running..." : "Run V2 Preview QA"}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleRenderSelectedPublicPage}
                                        disabled={loading || !previewSessionId}
                                    >
                                        Open Selected Page
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={handleClaimV2Preview}
                                        disabled={loading || !previewSessionId}
                                    >
                                        Claim Preview
                                    </button>
                                </div>

                                {(previewSessionId || claimedProjectId) && (
                                    <div style={styles.metaBox}>
                                        {previewSessionId && <div>Preview Session: <strong>{previewSessionId}</strong></div>}
                                        {claimedProjectId && <div>Claimed Project: <strong>{claimedProjectId}</strong></div>}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div style={styles.rightPanel}>
                            <div style={styles.resultHeader}>
                                <h3>QA Result</h3>
                                <div style={styles.tabRow}>
                                    <button type="button" onClick={() => setViewMode("qa")} style={viewMode === "qa" ? styles.tabActive : styles.tab}>QA</button>
                                    <button type="button" onClick={() => setViewMode("html")} style={viewMode === "html" ? styles.tabActive : styles.tab}>HTML</button>
                                    <button type="button" onClick={() => setViewMode("css")} style={viewMode === "css" ? styles.tabActive : styles.tab}>CSS</button>
                                    <button type="button" onClick={() => setViewMode("js")} style={viewMode === "js" ? styles.tabActive : styles.tab}>JS</button>
                                </div>
                            </div>

                            {viewMode === "qa" && renderQaReport()}

                            {viewMode === "html" && (
                                <textarea
                                    value={htmlText || html}
                                    onChange={(e) => setHtmlText(e.target.value)}
                                    style={styles.outputArea}
                                    placeholder="Generated HTML will appear here."
                                />
                            )}

                            {viewMode === "css" && (
                                <textarea
                                    value={cssText}
                                    readOnly
                                    style={styles.outputArea}
                                    placeholder="Generated CSS will appear here."
                                />
                            )}

                            {viewMode === "js" && (
                                <textarea
                                    value={jsText}
                                    readOnly
                                    style={styles.outputArea}
                                    placeholder="Generated JS will appear here."
                                />
                            )}
                        </div>
                    </div>
                )}

                {flowMode === "project-crm-qa" && (
                    <div style={styles.workspaceGrid}>
                        <div style={styles.leftPanel}>
                            <div style={styles.controlsCard}>
                                <div style={styles.sectionHeader}>
                                    <div>
                                        <h3>CRM Redeploy QA</h3>
                                        <p>Use this after claiming a preview. It verifies CRM data renders into pages.</p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={loadProjectOptions}
                                        disabled={projectOptionsLoading}
                                    >
                                        {projectOptionsLoading ? "Loading..." : "Load Projects"}
                                    </button>
                                </div>

                                <div style={styles.formGrid}>
                                    <label style={styles.field}>
                                        <span>Project</span>
                                        <select
                                            value={projectQaId}
                                            onChange={(e) => setProjectQaId(e.target.value)}
                                            style={styles.select}
                                        >
                                            <option value="">Select project</option>
                                            {projectOptions.map((project) => {
                                                const id = project.id || project.Id;
                                                const name = project.projectName || project.ProjectName || project.businessName || project.BusinessName || id;

                                                return (
                                                    <option key={id} value={id}>
                                                        {name}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </label>

                                    <label style={styles.field}>
                                        <span>Module key</span>
                                        <select
                                            value={projectQaModuleKey}
                                            onChange={(e) => setProjectQaModuleKey(e.target.value)}
                                            style={styles.select}
                                        >
                                            {[
                                                "products", "collections", "programs", "coaches", "treatments", "doctors",
                                                "practice-areas", "team", "services", "specialists", "gallery", "menuItems",
                                                "specials", "trainers", "memberships", "properties", "listings", "agents"
                                            ].map((x) => (
                                                <option key={x} value={x}>{x}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <div style={styles.actionsRow}>
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleRunProjectCrmQa}
                                        disabled={loading}
                                    >
                                        Run CRM QA
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div style={styles.rightPanel}>
                            {projectCrmQaReport ? (
                                <div style={styles.qaReport}>
                                    <h3>CRM QA Result</h3>
                                    <pre style={styles.pre}>{JSON.stringify(projectCrmQaReport, null, 2)}</pre>
                                </div>
                            ) : (
                                <div style={styles.empty}>Run CRM QA to see results.</div>
                            )}
                        </div>
                    </div>
                )}

                {(flowMode === "renderer" || flowMode === "public-preview-qa") && (
                    <div style={styles.workspaceGrid}>
                        <div style={styles.leftPanel}>
                            <div style={styles.controlsCard}>
                                <h3>{flowMode === "renderer" ? "Renderer Only" : "V1/Public Preview QA"}</h3>
                                <p style={styles.helpText}>
                                    This mode is mainly for developers or V1 comparison. New QA should use V2 Preview QA first.
                                </p>

                                <div style={styles.formGrid}>
                                    <label style={styles.field}>
                                        <span>Vertical</span>
                                        <select
                                            value={selectedSample}
                                            onChange={(e) => handleSampleChange(e.target.value)}
                                            style={styles.select}
                                        >
                                            {Object.entries(samples).map(([key, sample]) => (
                                                <option key={key} value={key}>{sample.label}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label style={styles.field}>
                                        <span>Page</span>
                                        <select
                                            value={previewPage}
                                            onChange={(e) => setPreviewPage(e.target.value)}
                                            style={styles.select}
                                        >
                                            {availablePages.map((page) => (
                                                <option key={page.key} value={page.key}>{page.title || page.key}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <textarea
                                    value={jsonText}
                                    onChange={(e) => setJsonText(e.target.value)}
                                    style={styles.jsonArea}
                                />

                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleRender}
                                    disabled={loading}
                                >
                                    {loading ? "Running..." : "Run"}
                                </button>
                            </div>
                        </div>

                        <div style={styles.rightPanel}>
                            {renderQaReport()}
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
}

const styles = {
    page: {
        display: "grid",
        gap: 22,
        padding: 20
    },
    heroPanel: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 20,
        padding: 24,
        borderRadius: 24,
        background: "linear-gradient(135deg,#0f172a,#1e293b)",
        color: "#fff",
        boxShadow: "0 18px 48px rgba(15,23,42,.18)"
    },
    kicker: {
        fontSize: 12,
        fontWeight: 900,
        letterSpacing: ".16em",
        textTransform: "uppercase",
        color: "#93c5fd",
        marginBottom: 8
    },
    title: {
        margin: 0,
        fontSize: 30,
        fontWeight: 900
    },
    subtitle: {
        margin: "8px 0 0",
        maxWidth: 820,
        color: "#cbd5e1"
    },
    heroActions: {
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        justifyContent: "flex-end"
    },
    errorBanner: {
        padding: 14,
        borderRadius: 16,
        background: "#fef2f2",
        color: "#991b1b",
        border: "1px solid #fecaca",
        fontWeight: 700
    },
    infoBanner: {
        padding: 14,
        borderRadius: 16,
        background: "#eff6ff",
        color: "#1e40af",
        border: "1px solid #bfdbfe",
        fontWeight: 700
    },
    flowGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))",
        gap: 14
    },
    flowCard: {
        textAlign: "left",
        padding: 18,
        borderRadius: 20,
        border: "1px solid #e2e8f0",
        background: "#fff",
        cursor: "pointer",
        boxShadow: "0 10px 28px rgba(15,23,42,.06)"
    },
    flowCardActive: {
        borderColor: "#2563eb",
        boxShadow: "0 14px 36px rgba(37,99,235,.16)"
    },
    flowTop: {
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        alignItems: "center"
    },
    badge: {
        fontSize: 11,
        fontWeight: 900,
        padding: "4px 8px",
        borderRadius: 999,
        background: "#f1f5f9",
        color: "#475569"
    },
    badgeActive: {
        fontSize: 11,
        fontWeight: 900,
        padding: "4px 8px",
        borderRadius: 999,
        background: "#dbeafe",
        color: "#1d4ed8"
    },
    stepPills: {
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        marginTop: 12
    },
    workspaceGrid: {
        display: "grid",
        gridTemplateColumns: "minmax(360px,.9fr) minmax(420px,1.1fr)",
        gap: 18,
        alignItems: "start"
    },
    leftPanel: {
        display: "grid",
        gap: 16
    },
    rightPanel: {
        display: "grid",
        gap: 16,
        padding: 18,
        borderRadius: 22,
        background: "#fff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 10px 28px rgba(15,23,42,.06)"
    },
    sectionHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 14
    },
    verticalGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
        gap: 10
    },
    verticalCard: {
        textAlign: "left",
        padding: 14,
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        background: "#fff",
        cursor: "pointer"
    },
    verticalCardActive: {
        borderColor: "#2563eb",
        background: "#eff6ff"
    },
    guideCard: {
        padding: 18,
        borderRadius: 22,
        border: "1px solid #e2e8f0",
        background: "#fff"
    },
    guideGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
        gap: 16
    },
    controlsCard: {
        padding: 18,
        borderRadius: 22,
        border: "1px solid #e2e8f0",
        background: "#fff",
        boxShadow: "0 10px 28px rgba(15,23,42,.06)"
    },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
        gap: 12
    },
    field: {
        display: "grid",
        gap: 6,
        fontWeight: 800,
        color: "#334155"
    },
    select: {
        minHeight: 44,
        borderRadius: 12,
        border: "1px solid #cbd5e1",
        padding: "0 12px"
    },
    input: {
        minHeight: 44,
        borderRadius: 12,
        border: "1px solid #cbd5e1",
        padding: "0 12px"
    },
    actionsRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        marginTop: 14
    },
    metaBox: {
        marginTop: 14,
        padding: 12,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        fontSize: 13
    },
    resultHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12
    },
    tabRow: {
        display: "flex",
        gap: 8,
        flexWrap: "wrap"
    },
    tab: {
        border: "1px solid #cbd5e1",
        background: "#fff",
        borderRadius: 999,
        padding: "8px 12px",
        cursor: "pointer"
    },
    tabActive: {
        border: "1px solid #2563eb",
        background: "#2563eb",
        color: "#fff",
        borderRadius: 999,
        padding: "8px 12px",
        cursor: "pointer"
    },
    outputArea: {
        width: "100%",
        minHeight: 640,
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: 14,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 12
    },
    jsonArea: {
        width: "100%",
        minHeight: 420,
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        padding: 14,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 12,
        margin: "14px 0"
    },
    qaReport: {
        display: "grid",
        gap: 14
    },
    qaSection: {
        padding: 14,
        borderRadius: 16,
        background: "#f8fafc",
        border: "1px solid #e2e8f0"
    },
    qaBox: {
        padding: 12,
        borderRadius: 14,
        background: "#fff",
        border: "1px solid #e2e8f0",
        marginTop: 10
    },
    qaMeta: {
        fontSize: 13,
        color: "#475569"
    },
    statusBadge: {
        display: "inline-flex",
        width: "fit-content",
        padding: "8px 12px",
        borderRadius: 999,
        fontWeight: 900
    },
    statusPassed: {
        background: "#dcfce7",
        color: "#166534"
    },
    statusWarning: {
        background: "#fef3c7",
        color: "#92400e"
    },
    statusFailed: {
        background: "#fee2e2",
        color: "#991b1b"
    },
    qaError: {
        color: "#991b1b",
        fontWeight: 700
    },
    qaWarning: {
        color: "#92400e",
        fontWeight: 700
    },
    empty: {
        padding: 24,
        borderRadius: 18,
        background: "#f8fafc",
        color: "#64748b",
        textAlign: "center"
    },
    pre: {
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: 12,
        background: "#0f172a",
        color: "#dbeafe",
        padding: 14,
        borderRadius: 14,
        maxHeight: 640,
        overflow: "auto"
    },
    helpText: {
        color: "#64748b"
    }
};